import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Sparkles, ArrowRight, RotateCcw, Sun, Sunset, Moon, Compass, Brain, Heart, Zap, Smile, Leaf, ChevronLeft } from "lucide-react";

const QUESTIONS = [
  {
    key: "time",
    prompt: "When will you most likely use it?",
    helper: "We'll match the experience to your day.",
    options: [
      { value: "morning",   label: "Morning",       icon: Sun },
      { value: "afternoon", label: "Afternoon",     icon: Sunset },
      { value: "evening",   label: "Evening",       icon: Moon },
      { value: "any",       label: "Any time",      icon: Compass },
    ],
  },
  {
    key: "goal",
    prompt: "What are you hoping to feel?",
    helper: "Pick the closest fit — you can always change your mind.",
    options: [
      { value: "sleep",    label: "Better sleep",      icon: Moon },
      { value: "calm",     label: "Calm & relaxed",    icon: Smile },
      { value: "focus",    label: "Calm focus",        icon: Brain },
      { value: "lift",     label: "A daytime lift",    icon: Zap },
      { value: "recovery", label: "Recovery & rest",   icon: Heart },
    ],
  },
  {
    key: "experience",
    prompt: "How familiar are you with hemp products?",
    helper: "We'll match the strength to your experience.",
    options: [
      { value: "new",         label: "New to this",        icon: Leaf },
      { value: "some",        label: "Tried a few times",  icon: Sparkles },
      { value: "experienced", label: "Experienced",        icon: Heart },
    ],
  },
];

function scoreProduct(p, ans) {
  let s = 0;
  const effects = (p.effects || []).map((e) => e.toLowerCase());
  const strength = (p.strength || "").toLowerCase();

  // Goal match
  const goalMap = {
    sleep:   ["sleep"],
    calm:    ["calm", "relax", "unwind & relax"],
    focus:   ["focus", "calm focus"],
    lift:    ["lift", "daytime lift"],
    recovery:["recovery"],
  };
  const wanted = goalMap[ans.goal] || [];
  for (const w of wanted) {
    if (effects.some((e) => e.includes(w))) s += 4;
  }

  // Time-of-day soft bias
  if (ans.time === "morning")     { if (effects.some((e) => e.includes("focus") || e.includes("lift"))) s += 2; }
  if (ans.time === "afternoon")   { if (effects.some((e) => e.includes("calm")  || e.includes("relax") || e.includes("lift"))) s += 1; }
  if (ans.time === "evening")     { if (effects.some((e) => e.includes("sleep") || e.includes("relax") || e.includes("recovery"))) s += 2; }

  // Experience → strength bias
  if (ans.experience === "new") {
    if (strength === "mild") s += 3;
    if (strength === "strong") s -= 3;
  } else if (ans.experience === "some") {
    if (strength === "balanced") s += 2;
    if (strength === "mild") s += 1;
  } else if (ans.experience === "experienced") {
    if (strength === "strong") s += 2;
    if (strength === "balanced") s += 1;
  }

  // Tiebreakers: featured > is_new
  if (p.featured) s += 0.5;
  if (p.is_new) s += 0.25;
  return s;
}

export default function HelpMeChoose() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };

  const reset = () => { setStep(0); setAnswers({}); };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const finished = step >= QUESTIONS.length;
  const recommendation = useMemo(() => {
    if (!finished || products.length === 0) return null;
    const scored = products
      .map((p) => ({ p, s: scoreProduct(p, answers) }))
      .sort((a, b) => b.s - a.s);
    return scored[0]?.p || null;
  }, [finished, products, answers]);

  const q = QUESTIONS[step];

  return (
    <section data-testid="help-me-choose" className="relative bg-sand/85 backdrop-blur-sm border-y border-white/20 py-20 lg:py-28 overflow-hidden">
      {/* Subtle copper accent */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-copper/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2">
            <Sparkles size={14} className="text-copper" strokeWidth={2} />
            <span className="eyebrow">Help Me Choose</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">
            Not sure where to start? <span className="italic text-copper">We'll guide you.</span>
          </h2>
          <p className="mt-5 text-base text-charcoal/85 leading-relaxed font-light">
            Answer three quick questions and we'll recommend the product Texans like you reach for most.
          </p>
        </div>

        {/* Progress */}
        <div className="mt-12 max-w-md mx-auto">
          <div className="flex gap-1.5">
            {QUESTIONS.map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 transition-colors ${
                  i < step ? "bg-copper" : i === step ? "bg-copper/40" : "bg-tan/30"
                }`}
              />
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between text-[10.5px] tracking-[0.25em] uppercase text-charcoal/60">
            <button
              onClick={goBack}
              disabled={step === 0}
              data-testid="hmc-back"
              className="inline-flex items-center gap-1 disabled:opacity-30 enabled:hover:text-navy"
            >
              <ChevronLeft size={12} /> Back
            </button>
            <span data-testid="hmc-progress">
              {finished ? "Your match" : `Step ${step + 1} of ${QUESTIONS.length}`}
            </span>
            <button
              onClick={reset}
              data-testid="hmc-reset"
              className="inline-flex items-center gap-1 hover:text-navy"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="mt-12">
          {!finished && q && (
            <div data-testid={`hmc-step-${step}`} className="animate-fade-up">
              <div className="text-center">
                <h3 className="font-serif text-2xl sm:text-3xl text-navy">{q.prompt}</h3>
                <p className="mt-3 text-sm text-charcoal/70">{q.helper}</p>
              </div>
              <div className={`mt-10 grid gap-3 ${q.options.length === 5 ? "grid-cols-2 md:grid-cols-5" : q.options.length === 4 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 md:grid-cols-3"}`}>
                {q.options.map((o) => (
                  <button
                    key={o.value}
                    data-testid={`hmc-option-${q.key}-${o.value}`}
                    onClick={() => choose(q.key, o.value)}
                    className="group float-card bg-white border border-tan/40 hover:border-copper transition-all px-5 py-7 flex flex-col items-center text-center"
                  >
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:bg-copper/10 transition-colors">
                      <o.icon size={22} strokeWidth={1.5} className="text-copper" />
                    </div>
                    <div className="mt-4 font-serif text-base text-navy leading-tight">{o.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {finished && (
            <div data-testid="hmc-result" className="animate-fade-up max-w-3xl mx-auto">
              {recommendation ? (
                <div className="bg-sand border border-tan/50 overflow-hidden grid grid-cols-1 md:grid-cols-5">
                  <div className="md:col-span-2 aspect-[4/5] md:aspect-auto overflow-hidden bg-white">
                    {recommendation.image_url && (
                      <img src={mediaUrl(recommendation.image_url)} alt={recommendation.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="md:col-span-3 p-8 lg:p-10 flex flex-col">
                    <div className="inline-flex items-center gap-2 self-start">
                      <Sparkles size={14} className="text-copper"/>
                      <span className="eyebrow">Recommended for you</span>
                    </div>
                    <h3 className="font-serif text-3xl sm:text-4xl text-navy mt-4 leading-tight">{recommendation.name}</h3>
                    <p className="mt-3 text-base text-charcoal/85 leading-relaxed">{recommendation.short_description}</p>

                    {recommendation.effects?.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-1.5">
                        {recommendation.effects.map((e) => (
                          <span key={e} className="bg-navy text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.18em]">{e}</span>
                        ))}
                      </div>
                    )}

                    {(recommendation.strength || recommendation.onset || recommendation.duration) && (
                      <div className="mt-5 grid grid-cols-3 gap-3 text-xs text-charcoal/80 border-t border-tan/40 pt-5">
                        {recommendation.strength && (
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Strength</div>
                            <div className="text-navy mt-1">{recommendation.strength}</div>
                          </div>
                        )}
                        {recommendation.onset && (
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Onset</div>
                            <div className="text-navy mt-1">{recommendation.onset}</div>
                          </div>
                        )}
                        {recommendation.duration && (
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/55">Duration</div>
                            <div className="text-navy mt-1">{recommendation.duration}</div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-7 flex flex-col sm:flex-row gap-3">
                      <Link
                        to={`/shop/${recommendation.slug}`}
                        data-testid="hmc-view-product"
                        className="bg-navy text-white px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-charcoal transition-colors inline-flex items-center justify-center gap-2"
                      >
                        View this product <ArrowRight size={14} />
                      </Link>
                      <button
                        onClick={reset}
                        data-testid="hmc-try-again"
                        className="border border-navy text-navy px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-navy hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                      >
                        <RotateCcw size={14}/> Try again
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-sand border border-tan/50 p-10 text-center">
                  <p className="text-charcoal/80">We couldn't find a perfect match. Browse the full catalog or chat with our team.</p>
                  <div className="mt-6 flex justify-center gap-3">
                    <Link to="/shop" className="bg-navy text-white px-7 py-3 text-xs uppercase tracking-[0.22em] hover:bg-charcoal">Browse catalog</Link>
                    <button onClick={reset} className="border border-navy text-navy px-7 py-3 text-xs uppercase tracking-[0.22em] hover:bg-navy hover:text-white">Try again</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
